<template lang="pug">
  .log-sleep
    .log-sleep-content
      .log-sleep-header Enter your time into and out of bed to calculate your total sleep.
      .sleep-times-container
        .sleep-time-group
          .sleep-time-label Bedtime
          .sleep-time-input
            .time-select-group
              select.time-select(v-model="bedtimeHour")
                option(v-for="hour in hours" :key="hour" :value="hour") {{ formatHour(hour) }}
              span :
              select.time-select(v-model="bedtimeMinute")
                option(v-for="minute in minutes" :key="minute" :value="minute") {{ formatMinute(minute) }}
              span.time-separator
              select.time-select(v-model="bedtimePeriod")
                option(value="AM") AM
                option(value="PM") PM

        .sleep-time-group
          .sleep-time-label Wake up time
          .sleep-time-input
            .time-select-group
              select.time-select(v-model="wakeHour")
                option(v-for="hour in hours" :key="hour" :value="hour") {{ formatHour(hour) }}
              span :
              select.time-select(v-model="wakeMinute")
                option(v-for="minute in minutes" :key="minute" :value="minute") {{ formatMinute(minute) }}
              span.time-separator
              select.time-select(v-model="wakePeriod")
                option(value="AM") AM
                option(value="PM") PM

      .total-sleep-display(v-if="totalSleepTimeText")
        | Total sleep: 
        span {{ totalSleepTimeText }}
      .save-button
        WtButton(
          @click.native="saveEntry"
          :loading="savingSleep"
        ) Next
</template>

<script>
import { mapActions } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";

export default {
  name: "LogSleep",

  components: {
    WtTitle,
    WtButton,
    WtSvgIcon
  },

  mixins: [analyticsTrackingMixin, dateUtilityMixin],

  props: {
    uniqueKey: {
      type: String,
      default: null
    },

    entryItem: {
      type: Object,
      default: () => ({}),
      required: true
    },

    entryDate: {
      type: String,
      default: ""
    },

    formData: {
      type: FormData,
      required: true,
      validator: value => {
        return value instanceof FormData;
      }
    },

    saveEntryWrapper: {
      type: Function,
      required: true
    },

    disableSave: {
      type: Boolean,
      default: true
    },

    disableReset: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      startTime: new Date(new Date().setHours(22, 30, 0)),
      endTime: new Date(new Date().setHours(6, 30, 0)),
      savingSleep: false,
      hours: Array.from({ length: 12 }, (_, i) => (i + 1).toString()),
      minutes: Array.from({ length: 60 }, (_, i) =>
        i.toString().padStart(2, "0")
      ),
      bedtimeHour: "11",
      bedtimeMinute: "00",
      bedtimePeriod: "PM",
      wakeHour: "07",
      wakeMinute: "00",
      wakePeriod: "AM"
    };
  },

  computed: {
    // Convert Date objects to time strings for inputs
    bedtimeString: {
      get() {
        return this.formatTimeForInput(this.startTime);
      },
      set(value) {
        this.startTime = this.parseTimeFromInput(value, 22, 30);
      }
    },

    waketimeString: {
      get() {
        return this.formatTimeForInput(this.endTime);
      },
      set(value) {
        this.endTime = this.parseTimeFromInput(value, 6, 30);
      }
    },

    sleepHourMinutes() {
      const startObj = this.moment(this.startTime);
      const endObj = this.moment(this.endTime);
      let hourDiff = endObj.hours() - startObj.hours();
      let minDiff = endObj.minutes() - startObj.minutes();

      if (hourDiff < 0) hourDiff = 24 + hourDiff;
      else if (hourDiff === 0 && minDiff < 0) hourDiff = 23;

      if (minDiff < 0) {
        minDiff = 60 + minDiff;
        hourDiff = hourDiff - 1;
      }
      return { hourDiff, minDiff };
    },

    totalSleepTime() {
      return parseFloat(
        // sends sleep time to db as total minutes
        (
          this.sleepHourMinutes.hourDiff * 60 +
          this.sleepHourMinutes.minDiff
        ).toString()
      ).toFixed(2);
    },

    totalSleepTimeText() {
      return `${this.sleepHourMinutes.hourDiff} hours, ${this.sleepHourMinutes.minDiff} mins`;
    },

    currentMetric() {
      return this._.find(this.entryItem.wellnessMetrics, {
        type: "sleep"
      });
    },

    currentMetricValue() {
      return this._.get(this.currentMetric, "value", 0);
    },

    currentMetricText() {
      return this._.get(this.currentMetric, "text", "");
    },

    currentStartTime() {
      return this.moment(
        this._.get(this.currentMetricText.split(","), "[0]", "")
      )._d;
    },

    currentEndTime() {
      return this.moment(
        this._.get(this.currentMetricText.split(","), "[1]", "")
      )._d;
    },

    setFormData() {
      if (!this._.isEmpty(this.entryItem)) {
        // either it's changed or it might have been changed and changed back to what it was
        return (
          this.startTime !== this.currentStartTime ||
          this.endTime !== this.currentEndTime ||
          this.formData.has("sleep")
        );
      }
      return true;
    }
  },

  watch: {
    totalSleepTime() {
      if (this.setFormData && !this.resettingForm) {
        this.formData.set("sleep", this.totalSleepTime);
        this.formData.set("sleepStart", this.startTime);
        this.formData.set("sleepEnd", this.endTime);
        this.$emit("formDataUpdated", true);
      }
    },

    entryItem: {
      handler() {
        if (this.currentMetricText && this.currentMetricText.includes(",")) {
          this.startTime = this.currentStartTime;
          this.endTime = this.currentEndTime;

          // Update bedtime dropdowns
          const bedtimeHours = this.startTime.getHours();
          const bedtimeIs12Hour = bedtimeHours % 12 || 12;
          this.bedtimeHour = bedtimeIs12Hour.toString();
          this.bedtimeMinute = this.startTime
            .getMinutes()
            .toString()
            .padStart(2, "0");
          this.bedtimePeriod = bedtimeHours >= 12 ? "PM" : "AM";

          // Update wake time dropdowns
          const wakeHours = this.endTime.getHours();
          const wakeIs12Hour = wakeHours % 12 || 12;
          this.wakeHour = wakeIs12Hour.toString();
          this.wakeMinute = this.endTime
            .getMinutes()
            .toString()
            .padStart(2, "0");
          this.wakePeriod = wakeHours >= 12 ? "PM" : "AM";
        }
      },
      immediate: true,
      deep: true
    },

    // Watch for uniqueKey changes to reset the form when switching dates
    uniqueKey: {
      handler() {
        if (this.currentMetricText && this.currentMetricText.includes(",")) {
          this.startTime = this.currentStartTime;
          this.endTime = this.currentEndTime;
        }
      },
      immediate: true
    },

    bedtimeHour() {
      this.updateStartTime();
    },
    bedtimeMinute() {
      this.updateStartTime();
    },
    bedtimePeriod() {
      this.updateStartTime();
    },
    wakeHour() {
      this.updateEndTime();
    },
    wakeMinute() {
      this.updateEndTime();
    },
    wakePeriod() {
      this.updateEndTime();
    }
  },

  mounted() {
    if (this.currentMetricText && this.currentMetricText.includes(",")) {
      this.startTime = this.currentStartTime;
      this.endTime = this.currentEndTime;
    }

    // Initialize bedtime dropdowns
    const bedtimeHours = this.startTime.getHours();
    const bedtimeIs12Hour = bedtimeHours % 12 || 12;
    this.bedtimeHour = bedtimeIs12Hour.toString();
    this.bedtimeMinute = this.startTime
      .getMinutes()
      .toString()
      .padStart(2, "0");
    this.bedtimePeriod = bedtimeHours >= 12 ? "PM" : "AM";

    // Initialize wake time dropdowns
    const wakeHours = this.endTime.getHours();
    const wakeIs12Hour = wakeHours % 12 || 12;
    this.wakeHour = wakeIs12Hour.toString();
    this.wakeMinute = this.endTime
      .getMinutes()
      .toString()
      .padStart(2, "0");
    this.wakePeriod = wakeHours >= 12 ? "PM" : "AM";
    this.trackFeatureView("tracking", "tracking_sleep");
  },

  methods: {
    ...mapActions("members", ["storeJournal", "updateJournal"]),

    formatTimeForInput(date) {
      if (!date) return "";
      const hours = date
        .getHours()
        .toString()
        .padStart(2, "0");
      const minutes = date
        .getMinutes()
        .toString()
        .padStart(2, "0");
      return `${hours}:${minutes}`;
    },

    parseTimeFromInput(timeString, defaultHour = 0, defaultMinute = 0) {
      if (!timeString) {
        return new Date(new Date().setHours(defaultHour, defaultMinute, 0));
      }

      const [hours, minutes] = timeString.split(":");
      return new Date(
        new Date().setHours(parseInt(hours), parseInt(minutes), 0)
      );
    },

    formatHour(hour) {
      return hour.padStart(2, "0");
    },

    formatMinute(minute) {
      return minute.padStart(2, "0");
    },

    updateStartTime() {
      const hour = parseInt(this.bedtimeHour);
      const minute = parseInt(this.bedtimeMinute);
      const is24Hour =
        this.bedtimePeriod === "AM"
          ? hour === 12
            ? 0
            : hour
          : hour === 12
          ? 12
          : hour + 12;
      this.startTime = new Date(new Date().setHours(is24Hour, minute, 0));
    },

    updateEndTime() {
      const hour = parseInt(this.wakeHour);
      const minute = parseInt(this.wakeMinute);
      const is24Hour =
        this.wakePeriod === "AM"
          ? hour === 12
            ? 0
            : hour
          : hour === 12
          ? 12
          : hour + 12;
      this.endTime = new Date(new Date().setHours(is24Hour, minute, 0));
    },

    saveEntry() {
      // Always send the values so the backend links the post to this metric
      this.formData.set("sleep", this.totalSleepTime);
      this.formData.set("sleepStart", this.startTime);
      this.formData.set("sleepEnd", this.endTime);

      this.savingSleep = true;
      this.saveEntryWrapper(this.storeJournal, this.updateJournal).finally(
        () => {
          this.savingSleep = false;
        }
      );
    },

    reset() {
      this.resettingForm = true;
      const items = ["sleep", "sleepStart", "sleepEnd"];
      items.forEach(item => {
        if (this.formData.has(item)) {
          this.formData.delete(item);
        }
      });
      this.startTime = new Date(new Date().setHours(22, 30, 0));
      this.endTime = new Date(new Date().setHours(6, 30, 0));
      this.$nextTick(() => {
        this.resettingForm = false;
        this.$emit("formReset", true);
      });
    }
  }
};
</script>

<style lang="sass" scoped>
.log-sleep
  height: 100%
  display: flex
  flex-direction: column
  justify-content: space-between

.log-sleep-content
  flex: 1
  flex-direction: column
  display: flex
  justify-content: center
  align-items: center
  gap: 20px

  .log-sleep-header
    width: 100%
    text-align: left
    font-weight: 600
    font-size: 20px
    line-height: 28px
    letter-spacing: 0px
    color: #1B1D20

  .sleep-times-container
    width: 100%
    display: flex
    gap: 16px

    @media (max-width: 768px)
      flex-direction: column
      align-items: center

      .sleep-time-group
        width: 100%
        align-items: center

      .sleep-time-input
        width: 100%
        max-width: 280px

      .time-select-group
        position: relative
        width: 100%
        justify-content: center

  .sleep-time-group
    flex: 1
    display: flex
    flex-direction: column
    gap: 8px

  .sleep-time-label
    text-align: center
    font-weight: 500
    font-size: 14px
    line-height: 20px
    color: #1B1D20

  .sleep-time-input
    height: 48px
    width: 196px

  .total-sleep-display
    width: 100%
    text-align: center
    font-weight: 500
    font-size: 14px
    line-height: 20px
    color: #5D6472
    padding-top: 10px
    span
      color: #1B1D20
  .save-button
    width: 100%

    ::v-deep .button
      width: 100% !important
      justify-content: center
      font-size: 16px
      line-height: 24px
      letter-spacing: 0px
      background-color: #2972F0
      font-weight: 600

      &:disabled
        background-color: #D1D5DB !important
        border-color: #D1D5DB !important
        color: #9CA3AF !important
        cursor: not-allowed

.centered-input
  width: 100%
  text-align: center
  font-size: 18px
  line-height: 24px
  font-weight: 500
  color: #5D6472
  border: 2px solid #E5E7EB
  border-radius: 8px
  padding: 12px
  background-color: white

  &:focus
    outline: none
    border-color: #3B82F6
    background-color: #F8FAFF

.log-sleep-form-controls
  margin-top: 20px

.time-select-group
  display: flex
  align-items: center
  gap: 12px
  background: white
  border: 1px solid #E5E7EB
  border-radius: 8px
  padding: 10px 20px 10px 20px
  font-size: 20px
  line-height: 28px

.time-select
  border: none
  background: transparent
  color: #1B1D20
  outline: none
  -webkit-appearance: none
  -moz-appearance: none
  appearance: none
  cursor: pointer
  text-align: center
  font-size: 20px
  line-height: 28px
  font-weight: 600px
  color: #5D6472

  option
    font-size: 16px
    line-height: 24px
    padding: 8px 12px
    color: #1B1D20
    background-color: white
    font-weight: 500
    text-align: center

    &:hover
      background-color: #F0F8FF
      color: #2972F0

  &::-webkit-scrollbar
    width: 8px
    background-color: #F5F5F5

  &::-webkit-scrollbar-thumb
    background-color: #D1D5DB
    border-radius: 4px

    &:hover
      background-color: #9CA3AF

.time-separator
  width: 1px
  height: 36px
  background-color: #D9D9D9
  opacity: 1
  display: inline-block
  vertical-align: middle
  transform: rotate(0deg)
  margin: 0 8px
</style>
